import { useRef, useState, useEffect } from 'react';
import { X, Sun, Moon, Upload, Fingerprint, Trash2 } from 'lucide-react';
import { ACCENTS, memberLabel } from '../lib/theme';
import { apiGet, apiSend } from '../lib/api';
import { startRegistration } from '@simplewebauthn/browser';

function Avatar({ entity, members, onPick, size = 48 }) {
  const ref = useRef();
  const av = members?.[entity]?.avatar;
  const initial = (members?.[entity]?.label || entity)[0].toUpperCase();
  const onFile = e => {
    const f = e.target.files?.[0];
    if (!f) return;
    const reader = new FileReader();
    reader.onload = () => {
      // downscale to keep storage small
      const img = new Image();
      img.onload = () => {
        const c = document.createElement('canvas');
        const s = 96; c.width = s; c.height = s;
        const ctx = c.getContext('2d');
        const min = Math.min(img.width, img.height);
        ctx.drawImage(img, (img.width - min) / 2, (img.height - min) / 2, min, min, 0, 0, s, s);
        onPick(c.toDataURL('image/jpeg', 0.8));
      };
      img.src = reader.result;
    };
    reader.readAsDataURL(f);
  };
  return (
    <button onClick={() => ref.current?.click()} title="Upload photo"
      className="relative rounded-full flex items-center justify-center overflow-hidden flex-shrink-0"
      style={{ width: size, height: size, background: av ? 'transparent' : 'var(--accent-grad)', border: '2px solid rgba(255,255,255,0.15)', cursor: 'pointer' }}>
      {av ? <img src={av} alt={entity} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
          : <span style={{ color: '#fff', fontWeight: 700, fontSize: size * 0.4 }}>{initial}</span>}
      <span className="absolute bottom-0 inset-x-0 flex items-center justify-center" style={{ background: 'rgba(0,0,0,0.5)', height: '40%' }}>
        <Upload size={size * 0.22} color="#fff" />
      </span>
      <input ref={ref} type="file" accept="image/*" hidden onChange={onFile} />
    </button>
  );
}

export default function SettingsModal({ open, onClose, theme, setTheme, entities, members, saveMembers }) {
  const [passkeys, setPasskeys] = useState([]);
  const [pkBusy, setPkBusy] = useState(false);
  const [pkMsg, setPkMsg] = useState('');
  const [pkEdit, setPkEdit] = useState(null);   // id being renamed
  const [pkEditVal, setPkEditVal] = useState('');

  const loadPasskeys = () => apiGet('/webauthn/list').then(r => r.json())
    .then(d => setPasskeys(d.passkeys || [])).catch(() => setPasskeys([]));

  useEffect(() => { if (open) loadPasskeys(); }, [open]);

  const registerPasskey = async () => {
    setPkBusy(true); setPkMsg('');
    try {
      const oR = await apiSend('/webauthn/register/options', {});
      const optionsJSON = await oR.json();
      const response = await startRegistration({ optionsJSON });
      const vR = await apiSend('/webauthn/register/verify', { response });
      const j = await vR.json();
      if (j.ok) { await loadPasskeys(); setPkMsg('Passkey added ✓'); }
      else setPkMsg(j.error ? `Failed: ${j.error}` : 'Registration failed');
    } catch (e) { setPkMsg(e.name === 'InvalidStateError' ? 'Passkey already registered' : 'Cancelled'); }
    finally { setPkBusy(false); }
  };

  const removePasskey = async (id) => {
    setPkBusy(true);
    try { await apiSend('/webauthn/remove', { id }); await loadPasskeys(); setPkMsg(''); }
    catch { /* ignore */ } finally { setPkBusy(false); }
  };

  const commitRename = async (id) => {
    const name = pkEditVal.trim();
    setPkEdit(null);
    if (name) { await apiSend('/webauthn/rename', { id, name }).catch(() => {}); await loadPasskeys(); }
  };

  if (!open) return null;

  const setMemberField = (entity, field, val) => {
    saveMembers({ ...members, [entity]: { ...members[entity], [field]: val } });
  };

  // modal is always dark — scope dark text vars locally so light mode stays readable
  const darkVars = { '--text': '#f5f0e8', '--text-dim': 'rgba(255,255,255,0.55)', '--text-mute': 'rgba(255,255,255,0.4)' };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(4px)' }} onClick={onClose}>
      <div onClick={e => e.stopPropagation()} className="w-full max-w-lg rounded-2xl overflow-hidden" style={{
        ...darkVars,
        background: 'linear-gradient(150deg, #1e1c24 0%, #161419 100%)', border: '1px solid rgba(255,255,255,0.1)', boxShadow: '0 30px 80px rgba(0,0,0,0.6)', maxHeight: '85vh', display: 'flex', flexDirection: 'column' }}>
        <div className="flex items-center justify-between px-6 py-4" style={{ borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
          <h2 className="font-display" style={{ fontSize: '20px', fontWeight: 700, color: 'var(--text)' }}>Settings</h2>
          <button onClick={onClose} style={{ color: 'var(--text-dim)' }}><X size={22} /></button>
        </div>

        <div className="px-6 py-5 space-y-7" style={{ overflowY: 'auto' }}>
          {/* Appearance */}
          <div>
            <p style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text-dim)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '12px' }}>Mode</p>
            <div className="flex gap-2">
              {[['dark', Moon], ['light', Sun]].map(([m, Icon]) => (
                <button key={m} onClick={() => setTheme(t => ({ ...t, mode: m }))}
                  className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium capitalize transition-all"
                  style={{ background: theme.mode === m ? 'var(--accent-grad)' : 'rgba(255,255,255,0.05)', color: theme.mode === m ? '#fff' : 'rgba(255,255,255,0.6)', border: '1px solid rgba(255,255,255,0.08)' }}>
                  <Icon size={16} />{m}
                </button>
              ))}
            </div>
          </div>

          {/* Accent themes */}
          <div>
            <p style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text-dim)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '12px' }}>Accent</p>
            <div className="flex gap-3">
              {Object.entries(ACCENTS).map(([key, a]) => (
                <button key={key} onClick={() => setTheme(t => ({ ...t, accent: key }))} title={a.name}
                  className="flex flex-col items-center gap-1.5">
                  <span style={{ width: 40, height: 40, borderRadius: '12px', background: `linear-gradient(135deg, ${a.c1}, ${a.c2})`,
                    border: theme.accent === key ? '2px solid #fff' : '2px solid transparent', boxShadow: theme.accent === key ? `0 0 14px ${a.glow}` : 'none' }} />
                  <span style={{ fontSize: '11px', color: theme.accent === key ? 'var(--text)' : 'rgba(255,255,255,0.4)' }}>{a.name}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Members */}
          <div>
            <p style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text-dim)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '12px' }}>Members</p>
            <div className="space-y-3">
              {entities.map(e => (
                <div key={e} className="flex items-center gap-3">
                  <Avatar entity={e} members={members} onPick={d => setMemberField(e, 'avatar', d)} />
                  <div className="flex-1">
                    <input value={members?.[e]?.label || ''} placeholder={memberLabel(e, members)}
                      onChange={ev => setMemberField(e, 'label', ev.target.value)}
                      className="w-full px-3 py-2 rounded-lg text-sm outline-none"
                      style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', color: 'var(--text)' }} />
                    {e === 'PRANAV' && <p style={{ fontSize: '11px', color: 'var(--text-mute)', marginTop: '3px' }}>this photo &amp; name appear on the login screen</p>}
                  </div>
                </div>
              ))}
            </div>
            <p style={{ fontSize: '12px', color: 'var(--text-mute)', marginTop: '12px' }}>
              Members come from your imported sheet. Add/remove members &amp; a separate login for family is coming next.
            </p>
          </div>

          {/* Passkey / biometric login */}
          <div>
            <p style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text-dim)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '12px' }}>Passkey</p>
            {/* registered passkeys — each row: name (click to rename) + trash */}
            {passkeys.length > 0 && (
              <div className="space-y-2 mb-3">
                {passkeys.map(pk => (
                  <div key={pk.id} className="flex items-center justify-between gap-2 px-3 py-2 rounded-xl"
                    style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)' }}>
                    <div className="flex items-center gap-2.5 flex-1 min-w-0">
                      <Fingerprint size={16} style={{ color: 'var(--accent)', flexShrink: 0 }} />
                      {pkEdit === pk.id ? (
                        <input autoFocus value={pkEditVal} onChange={e => setPkEditVal(e.target.value)}
                          onBlur={() => commitRename(pk.id)}
                          onKeyDown={e => { if (e.key === 'Enter') commitRename(pk.id); if (e.key === 'Escape') setPkEdit(null); }}
                          className="flex-1 px-2 py-1 rounded text-sm outline-none"
                          style={{ background: 'rgba(255,255,255,0.08)', border: '1px solid var(--accent)', color: 'var(--text)' }} />
                      ) : (
                        <button onClick={() => { setPkEdit(pk.id); setPkEditVal(pk.name); }} title="Click to rename"
                          className="text-sm text-left truncate" style={{ color: 'var(--text)', borderBottom: '1px dashed rgba(255,255,255,0.25)' }}>
                          {pk.name}
                        </button>
                      )}
                    </div>
                    <button onClick={() => removePasskey(pk.id)} disabled={pkBusy} title="Remove passkey"
                      className="flex-shrink-0 p-1.5 rounded-lg transition-colors" style={{ color: '#f87171' }}
                      onMouseEnter={e => { e.currentTarget.style.background = 'rgba(248,113,113,0.12)'; }}
                      onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}>
                      <Trash2 size={15} />
                    </button>
                  </div>
                ))}
              </div>
            )}
            <div className="flex items-center gap-3 flex-wrap">
              <button onClick={registerPasskey} disabled={pkBusy}
                className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold transition-all active:scale-95"
                style={{ background: 'var(--accent-grad)', color: '#fff', opacity: pkBusy ? 0.6 : 1 }}>
                <Fingerprint size={16} /> {passkeys.length > 0 ? 'Add another passkey' : 'Add a passkey'}
              </button>
              {pkMsg && <span style={{ fontSize: '13px', color: 'var(--text-dim)' }}>{pkMsg}</span>}
            </div>
            <p style={{ fontSize: '12px', color: 'var(--text-mute)', marginTop: '10px' }}>
              {passkeys.length > 0 ? 'Click a passkey name to rename it. Sign in with Face ID / fingerprint / device PIN — password still works as backup.'
                : 'Register this device for fingerprint / Face ID login. Your password keeps working as a fallback.'}
            </p>
          </div>
        </div>

        <div className="px-6 py-4 flex justify-end" style={{ borderTop: '1px solid rgba(255,255,255,0.08)' }}>
          <button onClick={onClose} className="px-5 py-2.5 rounded-xl text-sm font-semibold" style={{ background: 'var(--accent-grad)', color: '#fff' }}>Done</button>
        </div>
      </div>
    </div>
  );
}
